/**
 * Anti-undercut low-price safety check (pure, env-agnostic — no React,
 * no Firestore). Given the prices an operator just entered in the catalog
 * editor, flag the ones that are "unusually low" so the UI can ask for a
 * confirmation before the save writes.
 *
 * A price is unusually low when EITHER of:
 *   - it is below an absolute sanity floor (`floorSen`, default
 *     `LOW_PRICE_FLOOR_SEN`) — a wash priced below RM 3 is almost always a
 *     mistyped value (RM 1.50 for RM 15.00); OR
 *   - it is a steep drop from the current stored price for that vehicle
 *     type: new ≤ 50% of current (cut in half or more). This only applies
 *     when there IS a positive current price to compare against — new items
 *     / first-time prices can only trip the absolute floor.
 *
 * All amounts are integer sen. No floating-point division happens here; the
 * 50%-drop test uses `newSen * 2 <= currentSen` to stay exact.
 *
 * Rows priced 0 or below are treated as "not offered" by the catalog editor
 * (the save path skips them), so they are never flagged.
 */

/** Why a price was flagged: below the absolute floor, or a steep drop. */
export type LowPriceReason = 'floor' | 'drop';

/** One entered price to evaluate. `currentSen` is the stored price, if any. */
export interface LowPriceEntry {
  readonly id: string;
  readonly label: string;
  readonly newSen: number;
  readonly currentSen?: number;
}

/** An entry that tripped the check, with the reason it did. */
export interface LowPriceFlag extends LowPriceEntry {
  readonly reason: LowPriceReason;
}

/** Absolute sanity floor: a wash priced below RM 3.00 is almost always a typo. */
export const LOW_PRICE_FLOOR_SEN = 300;

/**
 * Return the entries that are unusually low. A steep drop is reported in
 * preference to the floor when both apply, because the drop message carries
 * the more useful "much lower than the current price" comparison.
 */
export function flagLowPrices(entries: readonly LowPriceEntry[], floorSen: number): LowPriceFlag[] {
  const flags: LowPriceFlag[] = [];
  for (const entry of entries) {
    const { newSen, currentSen } = entry;
    // Not offered / blank — the catalog editor never writes these, so an
    // intentionally-unset price is not "unusually low".
    if (newSen <= 0) continue;
    // Steep drop from a known, positive current price (new ≤ 50% of current).
    if (currentSen !== undefined && currentSen > 0 && newSen * 2 <= currentSen) {
      flags.push({ ...entry, reason: 'drop' });
      continue;
    }
    // Absolute sanity floor.
    if (newSen < floorSen) {
      flags.push({ ...entry, reason: 'floor' });
    }
  }
  return flags;
}
